import {
  CheckIcon,
  CrossIcon,
  LoadingIcon,
  WalletIcon,
  WarnIcon,
} from "@assets";
import Dialog, { DialogProps } from "@components/Dialog/Dialog";
import { to } from "@lib/utils";
import { walletAddressPreview } from "@models/utils";
import { Wallet } from "@models/wallet";
import { ReactNode, useState } from "react";
import ConnectMetamask from "./Metamask";
import {
  connectWallet,
  verifyWallet,
  WalletConnectResponse,
  WalletError,
  WalletExtension,
} from "./services";
import ConnectWalletConnect from "./WalletConnect";

const ConnectWallet = ({
  extension,
  children,
  onSuccess: onSuccess = () => {},
}: {
  extension: WalletExtension;
  children?: ReactNode;
  onSuccess?: (wallet: Wallet) => void;
}) => {
  const [connectWalletResponse, setConnectWalletResponse] =
    useState<WalletConnectResponse | null>(null);

  const [ongoing, setOngoing] = useState(false);

  const [addressPreview, setAddressPreview] = useState("");
  const [walletError, setWalletError] = useState<WalletError | null>(null);
  const [staticDialog, setStaticDialog] = useState<DialogProps | null>(null);

  const resetStates = () => {
    setOngoing(false);
    setStaticDialog(null);
    setWalletError(null);
    setAddressPreview("");
  };

  const closeAction = {
    text: "close",
    primary: true,
    onClick: () => resetStates(),
  };

  const loadingDialog: DialogProps = {
    onClose: () => resetStates(),
    icon: LoadingIcon,
    children: "Please continue in your wallet application.",
  };

  const changeAccountDialog: DialogProps = {
    onClose: () => resetStates(),
    icon: WarnIcon,
    title: "Change Account",
    actions: [closeAction],
    children: "Please change your active wallet in your wallet application.",
  };

  const successDialog: DialogProps = {
    onClose: () => resetStates(),
    icon: CheckIcon,
    title: "Wallet Connected",
    actions: [closeAction],
    children: "You have connected your wallet successfully!",
  };

  const handleError = (
    error: WalletError = WalletError.UNKNOWN_ERROR
  ): void => {
    if (ongoing) setWalletError(error);
    setOngoing(false);
    return;
  };

  const handleConnectWallet = async () => {
    setStaticDialog(loadingDialog);
    setOngoing(true);
    const { err: connectErr, res: connectWalletResponse } = await to(
      connectWallet(extension)
    );
    setStaticDialog(null);
    if (
      connectErr ||
      !connectWalletResponse ||
      !connectWalletResponse.walletAddress
    )
      return handleError(connectErr || WalletError.UNKNOWN_ERROR);

    setConnectWalletResponse(connectWalletResponse);
    setAddressPreview(
      walletAddressPreview(connectWalletResponse.walletAddress)
    );
  };

  const handleVerifyWallet = async () => {
    if (!connectWalletResponse) return setOngoing(false);
    setAddressPreview("");
    setOngoing(true);
    setStaticDialog(loadingDialog);
    console.log("handleVerifyWallet", extension, connectWalletResponse);
    const { err: verifyErr, res: wallet } = await to(
      verifyWallet(extension, connectWalletResponse)
    );
    console.log(wallet);
    setStaticDialog(null);
    if (verifyErr || !wallet)
      return handleError(verifyErr || WalletError.UNKNOWN_ERROR);
    return handleSuccess(wallet);
  };

  const handleSuccess = (wallet: Wallet) => {
    console.log("ConnectWallet", wallet);
    setStaticDialog(successDialog);
    setOngoing(false);
    return onSuccess(wallet);
  };

  const handleOnClick = () => {
    handleConnectWallet();
  };

  return (
    <>
      {extension == WalletExtension.METAMASK && (
        <ConnectMetamask
          onClick={handleOnClick}
          onSuccess={handleSuccess}
          onError={handleError}
        >
          {children}
        </ConnectMetamask>
      )}

      {extension == WalletExtension.WALLETCONNECT && (
        <ConnectWalletConnect
          onClick={handleOnClick}
          onSuccess={handleSuccess}
          onError={handleError}
        >
          {children}
        </ConnectWalletConnect>
      )}

      {staticDialog && (
        <Dialog
          isOpen={staticDialog.isOpen}
          onClose={staticDialog.onClose}
          title={staticDialog.title}
          actions={staticDialog.actions}
          loading={staticDialog.loading}
          icon={staticDialog.icon}
          onOpen={staticDialog.onOpen}
        >
          <p className="w-full text-center">{staticDialog.children}</p>
        </Dialog>
      )}

      <Dialog
        isOpen={ongoing && !!addressPreview}
        onClose={() => resetStates()}
        icon={WalletIcon}
        title="Confirm Wallet"
        actions={[
          {
            text: "Change Account",
            onClick: () => {
              setAddressPreview("");
              setStaticDialog(changeAccountDialog);
            },
          },
          {
            text: "Confirm",
            primary: true,
            onClick: () => handleVerifyWallet(),
          },
        ]}
      >
        <div className="flex flex-col justify-center items-center">
          <p>You are connecting the following address:</p>
          <p className="bg-neutral-850 px-4 py-2 rounded-full w-3/4 text-center mt-4">
            {addressPreview}
          </p>
        </div>
      </Dialog>

      <Dialog
        isOpen={
          !!walletError && Object.values(WalletError).includes(walletError)
        }
        title="Connection Error"
        icon={CrossIcon}
        onClose={() => resetStates()}
        actions={[closeAction]}
      >
        <div className="text-center">{walletError}</div>
      </Dialog>
    </>
  );
};

export default ConnectWallet;
